import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import NotificationManager from '@/managers/notification-manager.tsx';
import { NOTIFICATION_TYPES } from '@/constants/notifications';
import {
	BoutiqueCalendarEventConfig,
	BoutiqueCalendarRangeSeparator,
	BoutiqueEventPopoverCloseKey,
	BoutiqueEventPopoverIcon,
	BoutiqueEventPopoverScrollGroupCount,
	BoutiqueEventPopoverTexts,
	BoutiqueEventPopoverVisibleGroupAvatarCount
} from './boutique-calendar.constants';
import { BoutiqueCalendarLayoutEvent, BoutiqueEventGroup, BoutiqueEventPopoverPosition } from './boutique-calendar.types';
import { ClassroomGroup } from '../../boutique-classroom.types';
import {
	buildBoutiqueEventGroupList,
	countBoutiqueGroupStudents,
	formatBoutiqueEventDate,
	formatBoutiqueGroupSummary,
	formatBoutiqueStudentCount,
	getBoutiqueEventPopoverPosition,
	getBoutiqueInitials,
	isBoutiqueEventPast
} from './boutique-calendar.utils';
import '@/assets/styles/views/boutique-classroom/boutique-calendar/boutique-calendar-event-popover.styles.scss';

interface Props {
	event: BoutiqueCalendarLayoutEvent;
	groupList?: ClassroomGroup[];
	anchorElement: HTMLElement;
	onClose: () => void;
	onJoin: (event: BoutiqueCalendarLayoutEvent) => void;
	onEdit?: (event: BoutiqueCalendarLayoutEvent) => void;
}

const getGroupKey = (group: BoutiqueEventGroup, groupIndex: number): string => `${group.id ?? group.name}-${groupIndex}`;

export const BoutiqueCalendarEventPopover: React.FC<Props> = memo(({ event, groupList, anchorElement, onClose, onJoin, onEdit }) => {
	const panelRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<BoutiqueEventPopoverPosition | null>(null);

	useLayoutEffect(() => {
		const onUpdatePosition = (): void => {
			if (!anchorElement || !panelRef.current) {
				return;
			}

			setPosition(
				getBoutiqueEventPopoverPosition(
					anchorElement.getBoundingClientRect(),
					{ width: panelRef.current.offsetWidth, height: panelRef.current.offsetHeight },
					{ width: window.innerWidth, height: window.innerHeight }
				)
			);
		};

		onUpdatePosition();

		window.addEventListener('scroll', onUpdatePosition, true);
		window.addEventListener('resize', onUpdatePosition);

		return () => {
			window.removeEventListener('scroll', onUpdatePosition, true);
			window.removeEventListener('resize', onUpdatePosition);
		};
	}, [anchorElement, event]);

	useEffect(() => {
		const onKeyDown = (keyboardEvent: KeyboardEvent): void => {
			if (keyboardEvent.key === BoutiqueEventPopoverCloseKey) {
				onClose();
			}
		};

		const onOutsidePointerDown = (pointerEvent: MouseEvent): void => {
			const target = pointerEvent.target as Node;
			if (panelRef.current?.contains(target) || anchorElement?.contains(target)) {
				return;
			}

			onClose();
		};

		document.addEventListener('keydown', onKeyDown);
		document.addEventListener('mousedown', onOutsidePointerDown);

		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('mousedown', onOutsidePointerDown);
		};
	}, [anchorElement, onClose]);

	const eventConfig = BoutiqueCalendarEventConfig[event.eventType];
	const eventGroupList = useMemo(() => buildBoutiqueEventGroupList(event, groupList), [event, groupList]);
	const studentCount = useMemo(() => countBoutiqueGroupStudents(eventGroupList), [eventGroupList]);
	const [isGroupSectionOpen, setIsGroupSectionOpen] = useState(true);
	const [expandedGroupKeySet, setExpandedGroupKeySet] = useState<Set<string> | null>(null);
	const defaultExpandedGroupKeySet = useMemo(() => new Set(eventGroupList.length ? [getGroupKey(eventGroupList[0], 0)] : []), [eventGroupList]);
	const activeGroupKeySet = expandedGroupKeySet ?? defaultExpandedGroupKeySet;
	const isGroupListScrollable = eventGroupList.length > BoutiqueEventPopoverScrollGroupCount;

	const onToggleGroup = useCallback(
		(groupKey: string): void => {
			setExpandedGroupKeySet(currentSet => {
				const nextSet = new Set(currentSet ?? defaultExpandedGroupKeySet);
				if (nextSet.has(groupKey)) {
					nextSet.delete(groupKey);
				} else {
					nextSet.add(groupKey);
				}
				return nextSet;
			});
		},
		[defaultExpandedGroupKeySet]
	);

	const onCopyJoinUrl = useCallback(async (): Promise<void> => {
		if (!event.joinUrl) return;
		try {
			await navigator.clipboard.writeText(event.joinUrl);
			NotificationManager.showMessage(NOTIFICATION_TYPES.SUCCESS, BoutiqueEventPopoverTexts.joinLinkCopied);
		} catch {
			NotificationManager.showMessage(NOTIFICATION_TYPES.ERROR, BoutiqueEventPopoverTexts.copyJoinLink);
		}
	}, [event.joinUrl]);

	const panel = (
		<div
			ref={panelRef}
			className={clsx('boutique-event-popover', position?.placement, event.variant ?? eventConfig?.variant, { visible: Boolean(position) })}
			style={
				{
					'--boutique-popover-left': position?.left,
					'--boutique-popover-top': position?.top,
					'--boutique-popover-arrow-top': position?.arrowTop
				} as React.CSSProperties
			}>
			<span className="boutique-event-popover-arrow" />

			{onEdit && !isBoutiqueEventPast(event) && (
				<button
					type="button"
					className="boutique-event-popover-edit"
					title={BoutiqueEventPopoverTexts.editButton}
					aria-label={BoutiqueEventPopoverTexts.editButton}
					onClick={() => onEdit(event)}>
					<i className="icons-pen" />
					<span>{BoutiqueEventPopoverTexts.editButton}</span>
				</button>
			)}

			<button
				type="button"
				className="boutique-event-popover-close"
				title={BoutiqueEventPopoverTexts.closeButton}
				onClick={onClose}>
				<img
					src={BoutiqueEventPopoverIcon.close}
					alt=""
				/>
			</button>

			{Boolean(eventConfig?.decorationUrl) && (
				<img
					className="boutique-event-popover-decoration"
					src={eventConfig.decorationUrl}
					alt=""
				/>
			)}

			<div className="boutique-event-popover-content">
				{Boolean(eventConfig) && <span className="boutique-event-popover-badge">{eventConfig.label}</span>}

				<div className="boutique-event-popover-heading">
					<h3 className="boutique-event-popover-title">{event.title}</h3>
					<div className="boutique-event-popover-meta">
						<span className="boutique-event-popover-meta-item">
							<img
								src={BoutiqueEventPopoverIcon.calendar}
								alt=""
							/>
							{formatBoutiqueEventDate(event.dateKey)}
						</span>
						<span className="boutique-event-popover-meta-item">
							<img
								src={BoutiqueEventPopoverIcon.clock}
								alt=""
							/>
							{`${event.startTime}${BoutiqueCalendarRangeSeparator}${event.endTime}`}
						</span>
					</div>
				</div>

				{Boolean(event.description) && (
					<section className="boutique-event-popover-section">
						<h4 className="boutique-event-popover-section-title">{BoutiqueEventPopoverTexts.description}</h4>
						<p className="boutique-event-popover-description">{event.description}</p>
					</section>
				)}

				{Boolean(event.joinUrl) && (
					<section className="boutique-event-popover-section">
						<h4 className="boutique-event-popover-section-title">{BoutiqueEventPopoverTexts.joinLink}</h4>
						<button
							type="button"
							className="boutique-event-popover-link"
							title={BoutiqueEventPopoverTexts.copyJoinLink}
							onClick={onCopyJoinUrl}>
							<img
								className="boutique-event-popover-link-icon"
								src={BoutiqueEventPopoverIcon.video}
								alt=""
							/>
							<span className="boutique-event-popover-link-text">{event.joinUrl}</span>
							<img
								className="boutique-event-popover-link-copy"
								src={BoutiqueEventPopoverIcon.link}
								alt=""
							/>
						</button>
					</section>
				)}

				{Boolean(event.tagList?.length) && (
					<section className="boutique-event-popover-section">
						<h4 className="boutique-event-popover-section-title">{BoutiqueEventPopoverTexts.tags}</h4>
						<div className="boutique-event-popover-tags">
							{event.tagList?.map((tag, tagIndex) => (
								<span
									key={tagIndex}
									className={clsx('boutique-event-popover-tag', tag.variant)}>
									{tag.label}
								</span>
							))}
						</div>
					</section>
				)}

				{Boolean(eventGroupList.length) && (
					<section className="boutique-event-popover-section groups">
						<button
							type="button"
							className="boutique-event-popover-section-toggle"
							aria-expanded={isGroupSectionOpen}
							title={isGroupSectionOpen ? BoutiqueEventPopoverTexts.collapseSection : BoutiqueEventPopoverTexts.expandSection}
							onClick={() => setIsGroupSectionOpen(isOpen => !isOpen)}>
							<h4 className="boutique-event-popover-section-title">{BoutiqueEventPopoverTexts.groupsAndStudents}</h4>
							<span className="boutique-event-popover-section-summary">{formatBoutiqueGroupSummary(eventGroupList.length, studentCount)}</span>
							<i className={clsx('boutique-event-popover-chevron', isGroupSectionOpen ? 'icons-alt-arrow-up' : 'icons-alt-arrow-down')} />
						</button>

						{isGroupSectionOpen && (
							<div className={clsx('boutique-event-popover-group-list', { scrollable: isGroupListScrollable })}>
								{eventGroupList.map((group, groupIndex) => {
									const groupKey = getGroupKey(group, groupIndex);
									const isExpanded = activeGroupKeySet.has(groupKey);
									const visibleStudentList = group.studentList.slice(0, BoutiqueEventPopoverVisibleGroupAvatarCount);
									const hiddenStudentCount = Math.max(group.studentList.length - visibleStudentList.length, 0);

									return (
										<div
											key={groupKey}
											className={clsx('boutique-event-popover-group', { expanded: isExpanded })}>
											<button
												type="button"
												className="boutique-event-popover-group-header"
												aria-expanded={isExpanded}
												title={isExpanded ? BoutiqueEventPopoverTexts.collapseGroup : BoutiqueEventPopoverTexts.expandGroup}
												onClick={() => onToggleGroup(groupKey)}>
												<span className="boutique-event-popover-group-icon">
													<i className="icons-users-group-rounded" />
												</span>
												<span className="boutique-event-popover-group-info">
													<span className="boutique-event-popover-group-name">{group.name}</span>
													<span className="boutique-event-popover-group-count">{formatBoutiqueStudentCount(group.studentList.length)}</span>
												</span>
												{Boolean(visibleStudentList.length) && (
													<span className="boutique-event-popover-participant-list">
														{visibleStudentList.map(student => (
															<span
																key={student.id}
																className="boutique-event-popover-participant"
																title={student.name}>
																{student.imageUrl ? (
																	<img
																		src={student.imageUrl}
																		alt={student.name}
																	/>
																) : (
																	<span className="boutique-event-popover-participant-initials">{getBoutiqueInitials(student.name)}</span>
																)}
															</span>
														))}
														{Boolean(hiddenStudentCount) && <span className="boutique-event-popover-participant more">{`+${hiddenStudentCount}`}</span>}
													</span>
												)}
												<i className={clsx('boutique-event-popover-chevron', isExpanded ? 'icons-alt-arrow-down' : 'icons-alt-arrow-right')} />
											</button>

											{isExpanded && Boolean(group.studentList.length) && (
												<div className="boutique-event-popover-group-students">
													{group.studentList.map(student => (
														<span
															key={student.id}
															className="boutique-event-popover-student">
															{student.name}
														</span>
													))}
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}
					</section>
				)}
			</div>

			<footer className="boutique-event-popover-footer">
				<button
					type="button"
					className="boutique-event-popover-join"
					disabled={!event.joinUrl}
					onClick={() => onJoin(event)}>
					{BoutiqueEventPopoverTexts.joinButton}
				</button>
			</footer>
		</div>
	);

	return createPortal(panel, document.body);
});

BoutiqueCalendarEventPopover.displayName = 'BoutiqueCalendarEventPopover';

export default BoutiqueCalendarEventPopover;
